import { useEffect, useRef, useState } from "react";

const STEPS = [
  {
    n: 1,
    title: "Get an instant quote",
    desc: "Pick your vehicle, package, and add-ons in under a minute.",
  },
  {
    n: 2,
    title: "Pick a date that works",
    desc: "Choose a same-week slot at home, office, or apartment.",
  },
  {
    n: 3,
    title: "We come to you",
    desc: "Our mobile team arrives self-sufficient — water, power, all of it.",
  },
];

/**
 * 3-step "How it works" section with IntersectionObserver fade-in (no framer-motion to keep bundle small).
 * Mobile: vertical stack with gold connecting line. Desktop: 3-column with gold arrows.
 */
const HowItWorks = ({ heading = "How it works" }: { heading?: string }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!ref.current) return;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisible(true);
          io.disconnect();
        }
      },
      { threshold: 0.15 },
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, []);

  return (
    <section className="section-padding-sm bg-background relative overflow-hidden">
      <div className="container-luxury relative">
        <div className="text-center mb-12">
          <span className="badge-shimmer mb-4 inline-flex">Simple Process</span>
          <h2 className="font-display text-3xl md:text-5xl font-bold">
            {heading.split(" ").slice(0, -1).join(" ")}{" "}
            <span className="gold-gradient-text">{heading.split(" ").slice(-1)}</span>
          </h2>
        </div>

        <div ref={ref} className="relative max-w-5xl mx-auto">
          {/* Mobile vertical line */}
          <div
            className="md:hidden absolute left-[27px] top-6 bottom-6 w-px bg-gradient-to-b from-primary/0 via-primary/40 to-primary/0"
            aria-hidden="true"
          />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-4 relative">
            {STEPS.map((s, i) => (
              <div
                key={s.n}
                className="relative flex md:flex-col items-start md:items-center gap-4 md:gap-5 md:text-center"
                style={{
                  opacity: visible ? 1 : 0,
                  transform: visible ? "translateY(0)" : "translateY(20px)",
                  transition: `opacity 600ms ease-out ${i * 120}ms, transform 600ms ease-out ${i * 120}ms`,
                }}
              >
                <div className="relative shrink-0 w-14 h-14 rounded-full gold-gradient-bg flex items-center justify-center font-display font-bold text-xl text-primary-foreground shadow-lg shadow-primary/25 ring-4 ring-background">
                  {s.n}
                </div>

                {/* Desktop arrow connector */}
                {i < STEPS.length - 1 && (
                  <div
                    className="hidden md:block absolute top-7 left-[calc(50%+2rem)] right-[-2rem] h-px"
                    aria-hidden="true"
                    style={{
                      background:
                        "linear-gradient(90deg, hsl(var(--gold)/0.5), hsl(var(--gold)/0.15) 80%, transparent)",
                    }}
                  >
                    <span className="absolute right-0 -top-1.5 w-3 h-3 rotate-45 border-r border-t border-primary/40" />
                  </div>
                )}

                <div className="flex-1 md:flex-none">
                  <h3 className="font-display text-lg md:text-xl font-semibold mb-1.5">{s.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed md:max-w-[16rem] md:mx-auto">
                    {s.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
